import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { EmployeeLoginResult } from "../types";
import "./LoginCredentialsModal.css";

/**
 * Geçici şifre / giriş bilgileri penceresi — hesap açıldığında ya da şifre sıfırlandığında
 * geçici şifre burada BİR KEZ gösterilir. Personel kartı (İK) ve Kullanıcı Yönetimi (İK'sız) ortak kullanır.
 */
export interface LoginCredentialsModalProps {
  /** Kişinin adı (başlık) */
  who: string;
  result: EmployeeLoginResult;
  /** true: "Yeni geçici şifre"; false: ilk açılış "Giriş bilgileri" */
  reset?: boolean;
  onClose: () => void;
}

const I = {
  close: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>,
  key: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="8" cy="15" r="4" /><path d="m10.85 12.15 8.65-8.65M17 6l2 2M14 9l2 2" /></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="12" height="12" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="m5 12 5 5L20 7" /></svg>,
};

export const LoginCredentialsModal: React.FC<LoginCredentialsModalProps> = ({ who, result: r, reset, onClose }) => {
  const [copied, setCopied] = useState<"" | "ok" | "fail">("");
  const okRef = useRef<HTMLButtonElement>(null);
  const pwRef = useRef<HTMLElement>(null);
  const url = r.appUrl || window.location.origin;
  // Odak: açılınca Tamam'a, kapanınca açan öğeye döner; Escape sayfa genelinde çalışır (odak dışarıda kalsa da)
  useEffect(() => {
    const prev = document.activeElement as HTMLElement | null;
    const t = window.setTimeout(() => okRef.current?.focus(), 30);
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey, true);
    return () => { window.clearTimeout(t); document.removeEventListener("keydown", onKey, true); prev?.focus?.(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const copy = () => {
    const text = `Nedaris giriş bilgileri\nAdres: ${url}\nE-posta: ${r.email}\nGeçici şifre: ${r.temporaryPassword}\nRol: ${r.role}\nİlk girişte şifrenizi değiştirmeniz istenecek.`;
    const done = (state: "ok" | "fail") => { setCopied(state); window.setTimeout(() => setCopied(""), 2500); if (state === "fail") selectPassword(); };
    if (!navigator.clipboard?.writeText) return done("fail");
    navigator.clipboard.writeText(text).then(() => done("ok")).catch(() => done("fail"));
  };
  // Pano yoksa (http, izin reddi) şifre seçili bırakılır; kullanıcı Ctrl+C ile alır
  const selectPassword = () => { const el = pwRef.current; if (!el) return; const sel = window.getSelection(); const range = document.createRange(); range.selectNodeContents(el); sel?.removeAllRanges(); sel?.addRange(range); };
  return createPortal(
    <div className="lc-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} onClick={(e) => e.stopPropagation()}>
      <div className="lc-modal" role="dialog" aria-modal="true" aria-label={reset ? "Yeni geçici şifre" : "Giriş bilgileri"} onMouseDown={(e) => e.stopPropagation()}>
        <header className="lc-head">
          <span className="lc-ico">{I.key}</span>
          <div><span className="lc-kicker">{reset ? "Yeni geçici şifre" : "Giriş bilgileri"}</span><h3>{who}</h3></div>
          <button type="button" className="lc-ib" aria-label="Kapat" onClick={onClose}>{I.close}</button>
        </header>
        <div className="lc-body">
          <div className="lc-cred">
            <div><span>Adres</span><b>{url}</b></div>
            <div><span>E-posta</span><b>{r.email}</b></div>
            <div><span>Geçici şifre</span><b className="mono" ref={pwRef} onClick={selectPassword}>{r.temporaryPassword}</b></div>
            <div><span>Rol</span><b>{r.role}</b></div>
          </div>
          <p className={`lc-hint ${r.emailSent ? "ok" : "warn"}`}>
            {r.emailSent
              ? <><b>Giriş bilgileri kişinin e-postasına da gönderildi.</b> Bu şifre burada bir daha gösterilmez; kişi ilk girişte kendi şifresini belirleyecek. Kaybolursa "Şifremi unuttum" ile yeni şifre alır.</>
              : <><b>E-posta gönderilemedi (SMTP ayarlı değil ya da hata).</b> Bilgileri kişiye siz iletin; bu şifre bir daha gösterilmez. Kişi ilk girişte kendi şifresini belirleyecek; kaybolursa buradan yeni geçici şifre üretin.</>}
          </p>
        </div>
        <footer className="lc-foot">
          <button type="button" className={`lc-btn ${copied === "fail" ? "warn" : ""}`} onClick={copy}>{copied === "ok" ? <>{I.check} Kopyalandı</> : copied === "fail" ? <>Kopyalanamadı — şifre seçildi, Ctrl+C</> : <>{I.copy} Bilgileri kopyala</>}</button>
          <button ref={okRef} type="button" className="lc-btn primary" onClick={onClose}>Tamam</button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

export default LoginCredentialsModal;
